import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Import.css";

const MAX_VISIBLE = 200;

// Searchable dropdown of Iris fields, shown as "<Tab> › <Label>" (+ numeric
// field id). The tab name is not decoration: the real Iris catalog repeats
// labels like "Company Name"/"Address"/"Email" across 30+ tabs, so a mapping
// that only shows the label can silently point at the wrong tab.
// `emptyLabel` names the "no field" choice (default: ignore this column).
export default function FieldSelect({
	fields,
	value,
	onChange,
	emptyLabel = "— Ignore this column —",
}) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const wrapRef = useRef(null);
	const inputRef = useRef(null);

	const selected = useMemo(
		() =>
			value === null || value === undefined
				? null
				: fields.find((f) => String(f.id) === String(value)) || null,
		[fields, value]
	);

	// Close on outside click.
	useEffect(() => {
		if (!open) return undefined;
		const onDown = (e) => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) {
				setOpen(false);
				setQuery("");
			}
		};
		document.addEventListener("mousedown", onDown);
		return () => document.removeEventListener("mousedown", onDown);
	}, [open]);

	useEffect(() => {
		if (open && inputRef.current) inputRef.current.focus();
	}, [open]);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return fields;
		const parts = q.split(/\s+/);
		return fields.filter((f) => {
			const hay = `${f.label} ${f.tabName || ""} ${f.id}`.toLowerCase();
			return parts.every((p) => hay.includes(p));
		});
	}, [fields, query]);

	const visible = filtered.slice(0, MAX_VISIBLE);

	const pick = (fieldId) => {
		onChange(fieldId);
		setOpen(false);
		setQuery("");
	};

	return (
		<div className="fieldSelect" ref={wrapRef}>
			<button
				type="button"
				className={`fieldSelectToggle ${selected ? "" : "ignored"}`}
				onClick={() => setOpen((o) => !o)}>
				{selected ? (
					<>
						<span className="fieldSelectLabel">
							{selected.tabName ? `${selected.tabName} › ` : ""}
							{selected.label}
						</span>
						<span className="fieldSelectMeta">#{selected.id}</span>
					</>
				) : (
					<span className="fieldSelectLabel muted">{emptyLabel}</span>
				)}
				<span className="fieldSelectCaret">▾</span>
			</button>

			{open && (
				<div className="fieldSelectMenu">
					<input
						ref={inputRef}
						className="fieldSelectSearch"
						placeholder="Search fields (label, tab, id)…"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
					/>
					<div className="fieldSelectList">
						<div
							className={`fieldSelectOption ${
								selected ? "" : "selected"
							}`}
							onClick={() => pick(null)}>
							<span className="fieldSelectLabel muted">{emptyLabel}</span>
						</div>
						{visible.map((f) => (
							<div
								key={f.id}
								className={`fieldSelectOption ${
									selected && String(f.id) === String(selected.id)
										? "selected"
										: ""
								}`}
								onClick={() => pick(f.id)}>
								<span className="fieldSelectLabel">
									{f.tabName ? `${f.tabName} › ` : ""}
									{f.label}
								</span>
								<span className="fieldSelectMeta">
									#{f.id}
									{f.isRecordSet ? " · record set" : ""}
								</span>
							</div>
						))}
						{filtered.length === 0 && (
							<div className="fieldSelectEmpty">
								No fields match "{query}".
							</div>
						)}
						{filtered.length > MAX_VISIBLE && (
							<div className="fieldSelectEmpty">
								…{filtered.length - MAX_VISIBLE} more — refine your search.
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
